import React, { useState } from 'react';
import { Box, Text, useDisclosure, Button, HStack, } from '@chakra-ui/react';
import { DateRange } from 'react-date-range';
import { format } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Tooltip } from './Tooltip';

export const DateRangeSelector = ({ value, maxDays = null, maxDate = new Date(), align = 'left', onChange }) => {
  const { isOpen, onToggle } = useDisclosure();
  const [hasPickedStart, setHasPickedStart] = useState(false);

  // Track range user is currently picking
  const [tempRange, setTempRange] = useState(null);

  // Determine text to show
  const displayText = value?.[0] && value?.[1]
    ? `${format(value[0], 'MMM d, yyyy')} - ${format(value[1], 'MMM d, yyyy')}`
    : 'Select Date Range';

  const handleSelect = (ranges) => {
    const { startDate, endDate } = ranges.selection;

    const dayDiff = startDate && endDate
      ? (endDate - startDate) / (1000 * 60 * 60 * 24)
      : 0;

    // First click: user picks start date
    if (!hasPickedStart) {
      setHasPickedStart(true);
      setTempRange(ranges.selection);
      return;
    }

    // Second click: user finishes selection
    const isValidRange = startDate && endDate && (maxDays === null || dayDiff < maxDays);

    if (isValidRange) {
      onChange([startDate, endDate]);
      setTempRange(ranges.selection);
      setHasPickedStart(false);
      onToggle(); // close calendar
    } else {
      // Reset selection if invalid (e.g., too many days)
      setTempRange({
        startDate: startDate ?? new Date(),
        endDate: startDate ?? new Date(),
        key: 'selection',
      });
      setHasPickedStart(false); // restart selection
    }
  };

  const handleClear = () => {
    onChange([null, null]);
    setTempRange(null);
    setHasPickedStart(false);
    onToggle(); // also close picker
  };

  return (
    <Box position="relative">
      <HStack>
        <Tooltip label={maxDays ? `You can select up to ${maxDays} days` : ''} fontSize="sm" hasArrow>
          <Button onClick={onToggle} size="sm" variant="outline">
            {displayText}
          </Button>
        </Tooltip>

        {value?.[0] && value?.[1] && (
          <Button size="sm" variant="ghost" colorScheme="red" onClick={handleClear}>
            Clear
          </Button>
        )}
      </HStack>

      {isOpen && (
        <Box position="absolute" {...(align === 'right' ? { right: 0 } : { left: 0 })} zIndex="popover" borderRadius="md" borderWidth="1px" borderColor="gray.200" bg="white" shadow="md" p={2}>
          <DateRange
            editableDateInputs={false}
            moveRangeOnFirstSelection={false}
            maxDate={maxDate}
            showSelectionPreview
            onChange={handleSelect}
            ranges={[
              tempRange ?? {
                startDate: new Date(),
                endDate: new Date(),
                key: 'selection',
              },
            ]}
            rangeColors={['#3182CE']}
          />
          {maxDays && (
            <Box textAlign="center">
              <Text fontSize="md" color="brand">
                You can select up to {maxDays} days
              </Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
